/* eslint-disable global-require */
import React, { useEffect, useRef, useState } from 'react'
import envConfig from '@platformConfig/index'

const { planMfeRemoteUrl: MFE_REMOTE_URL } = envConfig
const CONTAINER_KEY = 'plan_mfe'
const EXPOSED_MODULE_KEY = './Activation'

async function initContainer(container) {
    try {
        await container.init(buildShareScope())
    } catch (err) {
        // Module federation can throw when init() runs more than once.
        const msg = err?.message || ''
        if (!/already initialized|already been initialized/i.test(msg)) {
            throw err
        }
    }
}

// The host runs webpack 4 and has no __webpack_share_scopes__, so provide the
// React share scope explicitly to avoid loading a second React copy in the MFE.
function buildShareScope() {
    const ReactLib = require('react')
    const ReactDOMLib = require('react-dom')
    const ReactDOMClient = require('react-dom/client')
    const ReactJSXRuntime = require('react/jsx-runtime')

    const ver = ReactLib.version

    const makeEntry = (mod) => ({
        [ver]: {
            get: () => Promise.resolve(() => mod),
            loaded: true,
            from: 'host',
            version: ver,
            shareConfig: {
                singleton: true,
                requiredVersion: '>=18.2 <20',
                strictVersion: false,
                eager: false
            }
        }
    })

    return {
        react: makeEntry(ReactLib),
        'react-dom': makeEntry(ReactDOMLib),
        'react-dom/client': makeEntry(ReactDOMClient),
        'react/jsx-runtime': makeEntry(ReactJSXRuntime)
    }
}

// Module-level promise so the remote is only loaded and initialised once per
// page, regardless of how many times <PlanMfeActivation> mounts.
let activationPromise = null

function loadActivationModule() {
    if (activationPromise) return activationPromise

    activationPromise = new Promise((resolve, reject) => {
        if (window[CONTAINER_KEY]) {
            resolve()
            return
        }

        // Guard against duplicate script tags (e.g. React strict-mode double invocation).
        const existing = document.querySelector('script[data-mfe="plan-mfe"]')
        if (existing) {
            if (existing.dataset.loaded === 'true') {
                resolve()
                return
            }
            existing.addEventListener('load', resolve)
            existing.addEventListener('error', () => reject(new Error(`Plan MFE script load error (${MFE_REMOTE_URL})`)))
            return
        }
        const script = document.createElement('script')
        script.src = MFE_REMOTE_URL
        script.async = true
        script.setAttribute('data-mfe', 'plan-mfe')
        script.onload = () => {
            script.dataset.loaded = 'true'
            resolve()
        }
        script.onerror = () => reject(new Error(`Failed to load Plan MFE from ${MFE_REMOTE_URL}`))
        document.head.appendChild(script)
    })
        .then(async () => {
            const container = window[CONTAINER_KEY]
            if (!container) {
                throw new Error(`Plan MFE container not registered after script load (${MFE_REMOTE_URL})`)
            }

            await initContainer(container)

            const factory = await container.get(EXPOSED_MODULE_KEY)
            const mod = factory()
            return mod.default || mod
        })
        .catch((err) => {
            // Reset so a subsequent render can retry after a transient network error.
            activationPromise = null
            throw err
        })

    return activationPromise
}

// The remote's Activation module has no error callback in its props contract
// (language, province, brand, flow, platform, channel, channelOrgId, phoneNumber,
// subscriber, plans, filters, bffBaseUrl, getToken, legal, copy, selectedPlanId,
// onSelect, onLearnMore, onFilterChange, onDurationChange, onFootnoteOpen, ... —
// there is no onError).  When its own `POST {bffBaseUrl}/v1/plans` call rejects
// it swallows the rejection, flips to an internal `error` status and renders an
// inline alert ("Couldn't load plans: POST /v1/plans failed: HTTP 404").  The
// host never hears about it, so onLoadError — which only ever fired for a failed
// remote *script* load — never ran and the plans page sat on the MFE's own error
// text instead of redirecting to the splash error page.
//
// Until the remote exposes an onError prop, watch its subtree for that alert node
// and surface it through the same onLoadError channel.  Both selectors below
// match the single error node the remote renders
// (`<div role="alert" data-testid="plan-mfe-error">`); no other node in the
// Activation bundle carries either attribute.
const MFE_ERROR_SELECTOR = '[data-testid="plan-mfe-error"], [role="alert"]'
export const MFE_RUNTIME_ERROR_CODE = 'MFE_PLANS_ERROR'

// The remote appends its own failure reason to the alert text, and for a BFF
// failure that reason carries the HTTP status ("...: POST /v1/plans failed:
// HTTP 404").  Pull the status out so the splash page shows the real code the
// user (or a support agent reading a screenshot) can act on, rather than a
// generic host-side label.  Anything without a status — a network drop, an
// aborted fetch, the remote's own "load blocked" precondition message — has no
// code to show, so it keeps the generic one.
const HTTP_STATUS_PATTERN = /\bHTTP\s+(\d{3})\b/

export const resolveMfeErrorCode = (message = '') => {
    const status = HTTP_STATUS_PATTERN.exec(message)?.[1]
    return status || MFE_RUNTIME_ERROR_CODE
}

function useMfeRuntimeErrorWatch(containerRef, isMounted, onError) {
    // The handler is rebuilt on every host render, so hold it in a ref and key
    // the observer off the mount alone — otherwise it would be torn down and
    // re-attached on each render.
    const onErrorRef = useRef(onError)
    const reportedRef = useRef(false)

    useEffect(() => {
        onErrorRef.current = onError
    }, [onError])

    useEffect(() => {
        const node = containerRef.current
        if (!isMounted || !node || typeof MutationObserver === 'undefined') return undefined

        const report = () => {
            if (reportedRef.current) return
            const errorNode = node.querySelector(MFE_ERROR_SELECTOR)
            if (!errorNode) return

            reportedRef.current = true
            observer.disconnect()

            const message = errorNode.textContent?.trim() || 'Plan MFE reported a load error'
            const err = new Error(message)
            err.code = resolveMfeErrorCode(message)
            // The remote localises this text itself ("Couldn't load plans" /
            // "Impossible de charger les forfaits") and it names the actual
            // failure, so it is fit to put in front of the user.  A failed
            // *module* load carries no description — those messages are internal
            // (they quote the remote URL) — and falls back to Contentful copy.
            err.description = message
            // eslint-disable-next-line no-console
            console.error('[PlanMfe] Remote reported a plan load failure', err)
            if (typeof onErrorRef.current === 'function') onErrorRef.current(err)
        }

        const observer = new MutationObserver(report)
        observer.observe(node, { childList: true, subtree: true })
        // The remote can already be in its error state by the time we attach
        // (e.g. a synchronous rejection, or a remount after a failed fetch).
        report()

        return () => observer.disconnect()
    }, [containerRef, isMounted])
}

/**
 * PlanMfeActivation
 *
 * Shell component that dynamically loads the Plan MFE remote and renders
 * its ./Activation module.  On SSR it renders an empty placeholder
 * (the MFE uses client-only rendering).  All props are forwarded verbatim
 * to the remote component — see the ActivationProps contract in the
 * integration doc §5.
 */
const PlanMfeActivation = (props) => {
    const [ActivationComponent, setActivationComponent] = useState(null)
    const [loadError, setLoadError] = useState(null)
    const [runtimeError, setRuntimeError] = useState(null)
    const containerRef = useRef(null)

    useMfeRuntimeErrorWatch(containerRef, Boolean(ActivationComponent), (err) => {
        setRuntimeError(err)
        if (typeof props.onLoadError === 'function') props.onLoadError(err)
    })

    useEffect(() => {
        // Client-side only — the remote component uses DOM APIs and cannot SSR.
        let cancelled = false

        loadActivationModule()
            .then((Component) => {
                if (!cancelled) setActivationComponent(() => Component)
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error('[PlanMfe] Failed to load remote Activation module', err)
                if (!cancelled) setLoadError(err)
                if (!cancelled && typeof props.onLoadError === 'function') {
                    props.onLoadError(err)
                }
            })

        return () => {
            cancelled = true
        }
    }, [])

    // A runtime failure inside the remote is reported through onLoadError, which
    // navigates away — drop the remote so its own error text does not flash
    // underneath while the redirect settles. With no handler wired up, leave the
    // remote mounted so its message stays on screen.
    if ((loadError || runtimeError) && typeof props.onLoadError === 'function') {
        return null
    }

    if (loadError) {
        return (
            <div className="flex justify-center py-8" role="alert">
                <span>Could not load plans. Please try again later.</span>
            </div>
        )
    }

    // Loading state — rendered both on SSR and during the remote script fetch.
    if (!ActivationComponent) {
        return <div className="kpre-plan-mfe-loading flex justify-center py-8" aria-busy="true" />
    }

    return (
        <div ref={containerRef} className="kpre-plan-mfe-host">
            <ActivationComponent {...props} />
        </div>
    )
}

export default PlanMfeActivation
